import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface ThinkingBreadcrumbProps {
  /** Accumulated reasoning steps */
  steps: string[];
  /** Whether the response is still streaming */
  isStreaming: boolean;
  /** Whether response text has started (suppress live status once text arrives) */
  hasContent: boolean;
}

export function ThinkingBreadcrumb({ steps, isStreaming, hasContent }: ThinkingBreadcrumbProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [displayText, setDisplayText] = useState("");
  const [visible, setVisible] = useState(false);
  const prevTextRef = useRef("");

  const latestStep = steps[steps.length - 1] ?? "";

  // Crossfade: when latest step changes, fade out then fade in
  useEffect(() => {
    if (!latestStep || latestStep === prevTextRef.current) return;
    setVisible(false);
    const timer = setTimeout(() => {
      setDisplayText(latestStep);
      setVisible(true);
      prevTextRef.current = latestStep;
    }, 200);
    return () => clearTimeout(timer);
  }, [latestStep]);

  // Live mode: show single updating line above tools
  if (isStreaming && steps.length > 0 && !hasContent) {
    return (
      <div className="cx-thinking-status" style={{ opacity: visible ? 1 : 0 }}>
        {displayText}
      </div>
    );
  }

  // Collapsed mode: show expandable trail after turn completes
  if (!isStreaming && steps.length > 0) {
    return (
      <div className="cx-thinking-trail-container">
        <button
          className="cx-thinking-trail"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          <svg
            className={`cx-thinking-trail-chevron${expanded ? " cx-thinking-trail-chevron--open" : ""}`}
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="currentColor"
          >
            <path d="M3 1l5 4-5 4z" />
          </svg>
          <span>
            {steps.length} {t("chat.reasoningSteps")}
          </span>
        </button>
        <div className={`cx-thinking-trail-steps${expanded ? " cx-thinking-trail-steps--open" : ""}`}>
          {steps.map((step, i) => (
            <div key={i} className="cx-thinking-trail-step">
              {step}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
}
